import React, {FC} from 'react';
import styled from 'styled-components';

import {extractInitials} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {Avatar} from '../../../components/common/avatars/avatar';
import {AvatarBackground} from '../../../components/common/avatars/avatarBackground';
import {AvatarImage} from '../../../components/common/avatars/avatarImage';
import {AvatarInitials} from '../../../components/common/avatars/avatarInitials';
import {StyledFlex} from '../../components/reportViewer/reportCommons';
import {MetricAvatarRenderer} from '../analyticsFormatHelpers';

/*
 * Props.
 */

interface AvatarValueProps {
  name: string;
  avatarUrl?: string;
}

/*
 * Styles.
 */

const StyledAvatar = styled(Avatar)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  margin-right: 4px;
`;

const StyledName = styled.div`
  ${ellipsis()}
`;

/*
 * Formatter.
 */

const AvatarValue: FC<AvatarValueProps> = (props) => {
  const {name, avatarUrl} = props;
  const initials = extractInitials(name);

  return (
    <StyledFlex>
      <StyledAvatar size={VisualSizesEnum.MEDIUM}>
        {avatarUrl ? renderAvatarImage(avatarUrl) : renderFallback(initials)}
      </StyledAvatar>
      <StyledName>{name}</StyledName>
    </StyledFlex>
  );
};

const renderAvatarImage = (avatarUrl: string) => (
  <>
    <UserThemeConsumer>{(theme) => <AvatarBackground color={theme.greys.white} />}</UserThemeConsumer>
    <AvatarImage imageUrl={avatarUrl} />
  </>
);

const renderFallback = (initials: string) => (
  <UserThemeConsumer>
    {(theme) => (
      <Avatar size={VisualSizesEnum.MEDIUM}>
        <AvatarBackground color={theme.greys.shade70} />
        <AvatarInitials size={VisualSizesEnum.MEDIUM} textColor={theme.invertedText} value={initials} />
      </Avatar>
    )}
  </UserThemeConsumer>
);

export const renderAvatarValue: MetricAvatarRenderer = ({value, avatarUrl}) => (
  <AvatarValue name={value} avatarUrl={avatarUrl} />
);
